import React from 'react';
import { BILLING_DECIMAL_PLACES } from '../../../constants/billingConstants';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Chip,
  Typography,
  CircularProgress,
  Switch,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import EditIcon from '@material-ui/icons/Edit';
import HistoryIcon from '@material-ui/icons/History';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';

/**
 * Operator Overrides Tab Component
 * Displays operator-specific billing item overrides
 */
const OperatorOverridesTab = ({
  classes,
  operators,
  selectedOperator,
  operatorSearch,
  search,
  operatorLoading,
  filteredOperatorItems,
  onSelectOperator,
  onOperatorSearchChange,
  onSearchChange,
  onOpenDetail,
  onOpenHistory,
  onExport,
  onCsvUpload,
  onBillingToggle,
}) => {
  return (
    <div className={classes.operatorPanel}>
      {/* Operator list */}
      <div>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          Operators
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          fullWidth
          placeholder="Search operators…"
          value={operatorSearch}
          onChange={(e) => onOperatorSearchChange(e.target.value)}
          style={{ marginBottom: 8 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <div className={classes.operatorList}>
          {operators
            .filter(
              (op) =>
                op.operatorId
                  .toLowerCase()
                  .includes(operatorSearch.toLowerCase()) ||
                op.name.toLowerCase().includes(operatorSearch.toLowerCase())
            )
            .map((op) => (
              <div
                key={op.operatorId}
                className={`${classes.operatorListItem} ${selectedOperator?.operatorId === op.operatorId
                  ? classes.operatorListItemSelected
                  : ''
                  }`}
                onClick={() => onSelectOperator(op)}
              >
                <Typography variant="body2">
                  <strong>{op.operatorId}</strong> — {op.name}
                </Typography>
                <Box
                  display="flex"
                  style={{ gap: 4, marginTop: 2, flexWrap: 'wrap' }}
                >
                  <Typography variant="caption" color="textSecondary">
                    {op.overrideCount} override
                    {op.overrideCount !== 1 ? 's' : ''}
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{ color: op.billingEnabled !== false ? '#2e7d32' : '#c62828' }}
                  >
                    Billing: {op.billingEnabled !== false ? 'Enabled' : 'Disabled'}
                  </Typography>
                  {op.reviewCount > 0 && (
                    <Chip
                      size="small"
                      label={`${op.reviewCount} to review`}
                      style={{
                        backgroundColor: '#fff3e0',
                        fontSize: 10,
                        height: 16,
                      }}
                    />
                  )}
                </Box>
              </div>
            ))}
        </div>
      </div>

      {/* Operator items panel */}
      <div>
        {!selectedOperator && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={200}
          >
            <Typography color="textSecondary">
              Select an operator to view their overrides
            </Typography>
          </Box>
        )}
        {selectedOperator && (
          <>
            <div className={classes.toolbar}>
              <Typography variant="subtitle2">
                {selectedOperator.name} ({selectedOperator.operatorId})
              </Typography>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className={classes.search}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                <Typography variant="body2" style={{ color: selectedOperator.billingEnabled !== false ? '#2e7d32' : '#c62828' }}>
                  Billing {selectedOperator.billingEnabled !== false ? 'Enabled' : 'Disabled'}
                </Typography>
                <Switch
                  checked={selectedOperator.billingEnabled !== false}
                  onChange={() => onBillingToggle(selectedOperator.operatorId, !(selectedOperator.billingEnabled !== false))}
                  color="primary"
                  size="small"
                />
              </Box>
              <Tooltip title="Download this operator's overrides as CSV">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={() => onExport('operator')}
                >
                  Export CSV
                </Button>
              </Tooltip>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PublishIcon />}
                component="label"
              >
                Import CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(e) => onCsvUpload(e, 'operator')}
                />
              </Button>
            </div>
            {operatorLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Billing Item Name</TableCell>
                      <TableCell>Display Name</TableCell>
                      <TableCell>Use Master Values</TableCell>
                      <TableCell align="right">Purchase $</TableCell>
                      <TableCell align="right">Sale $</TableCell>
                      <TableCell>GST</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOperatorItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="textSecondary" variant="body2">
                            No overrides for this operator
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredOperatorItems.map((row) => {
                      // Use the already-populated masterItemId from the API response
                      const master = row.masterItemId;
                      const effectivePurchase = (row.useMaster && master) ? master.purchasePrice : row.purchasePrice;
                      const effectiveSale = (row.useMaster && master) ? master.salePrice : row.salePrice;
                      return (
                        <TableRow
                          key={row._id}
                          hover
                          className={
                            row.excludeFromBilling ? classes.excluded : ''
                          }
                        >
                          <TableCell>
                            <Box
                              display="flex"
                              alignItems="center"
                              style={{ gap: 6 }}
                            >
                              {row.billingItemName}
                              {row.excludeFromBilling && (
                                <Chip
                                  size="small"
                                  label="Excluded"
                                  style={{
                                    backgroundColor: '#fff3e0',
                                    color: '#e65100',
                                    fontSize: 10,
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{row.displayItemName}</TableCell>
                          <TableCell>
                            <Switch checked={row.useMaster} color="primary" size="small" disabled />
                          </TableCell>
                          <TableCell align="right">
                            ${(effectivePurchase ?? 0).toFixed(BILLING_DECIMAL_PLACES)}
                            {row.useMaster && (
                              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
                                (master)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            ${(effectiveSale ?? 0).toFixed(BILLING_DECIMAL_PLACES)}
                            {row.useMaster && (
                              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
                                (master)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {row.GSTApplicable ? (
                              <Chip
                                size="small"
                                label={`${row.GSTPercentage}%`}
                                color="primary"
                              />
                            ) : (
                              <Chip size="small" label="No GST" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit override prices">
                              <IconButton
                                size="small"
                                onClick={() => onOpenDetail(row)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View change history">
                              <IconButton
                                size="small"
                                onClick={() => onOpenHistory(row)}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default OperatorOverridesTab;
